// The public pricing page (FR-73): published lookup bands for businesses and,
// when individuals pay, the subscription price. Amounts come in minor units
// (cents) in the API's currency; nothing about prices is written in code.
import { Loader, Paper, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorCode, errorMessage, unwrap } from "../../shared/session/errors";
import { useFormat } from "../../shared/ui/format";
import { FormError, WidePage } from "../../shared/ui/Page";

type Pricing = Schemas["PublicPricing"];
type Band = Schemas["PriceBand"];

/** bandRanges pairs each band with the first and last lookup of the month it covers (last 0: no limit). */
export function bandRanges(bands: readonly Band[]): { from: number; to: number; price: number }[] {
  let from = 1;
  return bands.map((b) => {
    const r = { from, to: b.up_to, price: b.price_minor };
    from = b.up_to + 1;
    return r;
  });
}

export function PricingPage() {
  const { t } = useTranslation();
  const brand = useBrand();
  const pricing = useQuery({
    queryKey: ["public", "pricing"],
    queryFn: () => unwrap(api.GET("/public/pricing")),
  });
  return (
    <WidePage title={t("pricing.title")}>
      {pricing.isPending ? (
        <Loader size="sm" aria-label={t("common.loading")} />
      ) : pricing.isError ? (
        errorCode(pricing.error) === "not_found" ? (
          <Text data-testid="pricing-unpublished">{t("pricing.not_published", { email: brand.support.email })}</Text>
        ) : (
          <FormError message={errorMessage(pricing.error, t("error.generic"))} />
        )
      ) : (
        <PricingView pricing={pricing.data} />
      )}
    </WidePage>
  );
}

function PricingView({ pricing: p }: { pricing: Pricing }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const number = (n: number) => new Intl.NumberFormat(i18n.language).format(n);
  const rate = new Intl.NumberFormat(i18n.language, { style: "percent", maximumFractionDigits: 2 }).format(
    p.vat_rate_bp / 10_000,
  );
  const range = (from: number, to: number) =>
    to === 0
      ? from === 1
        ? t("pricing.band_all")
        : t("pricing.band_above", { from: number(from) })
      : t("pricing.band_range", { from: number(from), to: number(to) });
  const forBusinesses = p.billing_model !== "individual" && p.resolve_bands.length > 0;

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Text data-testid="pricing-model">{t(`pricing.model_${p.billing_model}`)}</Text>
        <Text size="sm" c="dimmed">
          {t("pricing.intro", { currency: p.currency })}
        </Text>
      </Stack>
      {forBusinesses && (
        <Paper withBorder p="md" radius="md" component="section" aria-labelledby="pricing-business">
          <Stack gap="sm">
            <Title order={2} size="h4" id="pricing-business">
              {t("pricing.business_title")}
            </Title>
            <Text size="sm">{t("pricing.bands_hint")}</Text>
            <Table.ScrollContainer minWidth={260}>
              <Table verticalSpacing="xs" aria-label={t("pricing.business_title")} data-testid="pricing-bands">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("pricing.calls")}</Table.Th>
                    <Table.Th ta="right">{t("pricing.price")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {bandRanges(p.resolve_bands).map((b) => (
                    <Table.Tr key={b.from} data-testid="pricing-band">
                      <Table.Td>{range(b.from, b.to)}</Table.Td>
                      <Table.Td ta="right">{f.money(b.price, p.currency)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            {p.stk_price_minor !== undefined && (
              <Text size="sm" data-testid="pricing-stk">
                {t("pricing.stk", { price: f.money(p.stk_price_minor, p.currency) })}
              </Text>
            )}
          </Stack>
        </Paper>
      )}
      {p.subscription && (
        <Paper
          withBorder
          p="md"
          radius="md"
          component="section"
          aria-labelledby="pricing-individual"
          data-testid="pricing-subscription"
        >
          <Stack gap="xs">
            <Title order={2} size="h4" id="pricing-individual">
              {t("pricing.individual_title")}
            </Title>
            <Text fw={600}>
              {t("pricing.subscription", { price: f.money(p.subscription.price_minor, p.currency) })}
            </Text>
            {p.subscription.trial_days > 0 && (
              <Text size="sm">{t("pricing.trial", { count: p.subscription.trial_days })}</Text>
            )}
          </Stack>
        </Paper>
      )}
      <Text size="sm" c="dimmed" data-testid="pricing-vat">
        {t("pricing.vat", { rate })}
      </Text>
    </Stack>
  );
}
